import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiGet, apiPost, apiDelete } from '../services/apiService';
import { ENDPOINTS } from '../config/api';
import StatusPill from '../components/ui/StatusPill';
import Modal from '../components/ui/Modal';

// Add-ons the admin can comp/revoke (mirror the org-facing catalog). `key` = backend entitlement flag.
const ADDON_CATALOG = [
  { key: 'vendor_api', name: 'Vendor API integration', desc: "Connect vendors' inventory feeds (DLC for the Pro plan)." },
  { key: 'outbound_api', name: 'Outbound Inventory API', desc: 'Issue API keys so the org’s customers can pull its stock. Pick a tier below (defaults to Free). While comped, the org sees "Managed by administrator" and can’t self-serve change tiers. Revoke is instant and breaks their customers’ integrations.' },
];

const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';
const toArr = x => Array.isArray(x) ? x : (x?.items || x?.invoices || x?.data || []);
const fmtMoney = (v, cur = 'USD') => v == null ? '-' : (() => { try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(v); } catch { return `$${v}`; } })();

const Cell = ({ label, value }) => (
  <div style={{ padding: '12px 14px' }}>
    <div className="field-label" style={{ marginBottom: 5 }}>{label}</div>
    <div style={{ fontSize: 13.5, color: '#FAFAFA' }}>{value ?? '-'}</div>
  </div>
);

export default function OrganizationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [users, setUsers] = useState([]);
  const [sub, setSub] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [subBusy, setSubBusy] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [extendDays, setExtendDays] = useState(7);
  const [editSub, setEditSub] = useState(null);   // { plan, seats, comp, comp_until } when editing
  const [addons, setAddons] = useState([]);       // GET /addons for this org
  const [addonBusy, setAddonBusy] = useState('');
  const [addonUntil, setAddonUntil] = useState({}); // key -> comp_until date string
  const [addonTier, setAddonTier] = useState({});   // key -> tier key (outbound_api only)
  const [addonHint, setAddonHint] = useState('');
  const [apiTiers, setApiTiers] = useState([]);     // GET /admin/api-tiers -> Inventory API tier catalog

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, u, s, inv, ad, t] = await Promise.all([
        apiGet(ENDPOINTS.ADMIN.ORG_GET(id)),
        apiGet(ENDPOINTS.ADMIN.ORG_USERS(id)).catch(() => []),
        apiGet(ENDPOINTS.ADMIN.ORG_SUBSCRIPTION(id)).catch(() => null),
        apiGet(ENDPOINTS.ADMIN.ORG_INVOICES(id), { limit: 20 }).catch(() => []),
        apiGet(ENDPOINTS.ADMIN.ORG_ADDONS(id)).catch(() => []),
        apiGet(ENDPOINTS.ADMIN.API_TIERS).catch(() => []),
      ]);
      setOrg(o);
      setUsers(toArr(u));
      setSub(s || null);
      setInvoices(toArr(inv));
      setAddons(toArr(ad));
      setApiTiers(toArr(t));
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // Add-ons require the org to already be on a live Pro plan (comp or paid) - mirrors the
  // backend's own check, but done client-side so the admin can't click "Comp free" and hit
  // a 400 for an org that isn't eligible yet.
  const onPro = !!sub && /pro/i.test(sub.plan || sub.plan_id || sub.plan_name || '') && !['canceled', 'incomplete_expired', 'past_due', 'unpaid'].includes(sub.status);

  const suspended = (org?.status || 'active') === 'suspended';
  const toggleSuspend = async () => {
    if (!org) return;
    setBusy(true); setError('');
    try {
      await apiPost(suspended ? ENDPOINTS.ADMIN.ORG_ACTIVATE(id) : ENDPOINTS.ADMIN.ORG_SUSPEND(id), {});
      await load();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const extendTrial = async () => {
    setSubBusy('extend'); setError('');
    try { await apiPost(ENDPOINTS.ADMIN.ORG_EXTEND_TRIAL(id), { days: Number(extendDays) || 7 }); await load(); }
    catch (err) { setError(err.message); }
    finally { setSubBusy(''); }
  };
  const cancelSub = async () => {
    setConfirmCancel(false);
    setSubBusy('cancel'); setError('');
    try { await apiPost(ENDPOINTS.ADMIN.ORG_CANCEL_SUB(id), {}); await load(); }
    catch (err) { setError(err.message); }
    finally { setSubBusy(''); }
  };

  const openEdit = () => setEditSub({
    plan: sub?.plan || sub?.plan_id || sub?.plan_name || 'starter',
    seats: sub?.seats ?? sub?.seats_included ?? 1,
    comp: !!sub?.comp,
    comp_until: sub?.comp && sub?.current_period_end ? String(sub.current_period_end).slice(0, 10) : '',
  });
  const saveSub = async () => {
    if (!editSub.plan) { setError('Select a plan.'); return; }
    if (!(Number(editSub.seats) >= 1)) { setError('Enter the number of seats (at least 1).'); return; }
    if (!editSub.comp) { setError('Check "Free / comp" to grant this subscription.'); return; }
    if (!editSub.comp_until) { setError('Set an "Access until" date.'); return; }
    setSubBusy('save'); setError('');
    try {
      await apiPost(ENDPOINTS.ADMIN.ORG_UPDATE_SUB(id), {
        plan: editSub.plan,
        seats: Number(editSub.seats) || 1,
        comp: editSub.comp,
        ...(editSub.comp && editSub.comp_until ? { comp_until: editSub.comp_until } : {}),
      });
      setEditSub(null);
      await load();
    } catch (err) { setError(err.message); }
    finally { setSubBusy(''); }
  };

  // Comp an add-on free for this org (org must already be on Pro). comp_until optional.
  // For outbound_api, `tier` picks which Inventory API tier to grant (defaults to Free server-side).
  const compAddon = async (key) => {
    setAddonBusy(key); setAddonHint(''); setError('');
    try {
      const until = addonUntil[key];
      // The <select> falls back to showing its first <option> whenever no value has been
      // explicitly chosen yet (addonTier[key] === ''), so match that same fallback here -
      // otherwise clicking "Comp free" without touching the dropdown silently sends no tier
      // (server default) while the UI was visually showing a different tier as "selected".
      const tier = key === 'outbound_api' ? (addonTier[key] || apiTiers[0]?.key) : undefined;
      await apiPost(ENDPOINTS.ADMIN.ORG_ADDONS(id), { addon: key, ...(until ? { comp_until: until } : {}), ...(tier ? { tier } : {}) });
      setAddonUntil(u => ({ ...u, [key]: '' }));
      await load();
    } catch (err) {
      // Backend 400s when the org isn't on Pro - surface a clear next step instead of a raw error.
      if (/pro plan is required|requires? pro|on pro/i.test(err.message || '')) setAddonHint('Set this org to Pro first (Edit subscription → Pro, comp it), then comp the add-on.');
      else setError(err.message);
    } finally { setAddonBusy(''); }
  };
  const revokeAddon = async (key) => {
    setAddonBusy(key); setError('');
    try { await apiDelete(ENDPOINTS.ADMIN.ORG_ADDON(id, key)); await load(); }
    catch (err) { setError(err.message); }
    finally { setAddonBusy(''); }
  };

  if (loading) return <div className="page"><div style={{ padding: '48px 16px', textAlign: 'center', color: '#A1A1AA' }}>Loading…</div></div>;
  if (!org) return <div className="page"><div style={{ padding: '48px 16px', textAlign: 'center', color: '#A1A1AA' }}>{error || 'Organization not found.'}</div></div>;

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, gap: 24, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#A1A1AA', marginBottom: 4 }}>
            <span style={{ cursor: 'pointer' }} onClick={() => navigate('/organizations')}>Organizations</span> / <strong style={{ color: '#FAFAFA' }}>{org.name}</strong>
          </div>
          <h1 style={{ fontFamily: '"Geist","Inter",system-ui,sans-serif', fontWeight: 600, fontSize: 36, letterSpacing: '-0.02em', color: '#FAFAFA', margin: 0, lineHeight: 1.1, display: 'flex', alignItems: 'center', gap: 12 }}>
            {org.name}
            <StatusPill status={suspended ? 'on_hold' : 'active'} label={suspended ? 'Suspended' : (org.status || 'Active').replace(/^\w/, c => c.toUpperCase())} />
          </h1>
        </div>
        <button className={`btn ${suspended ? 'btn-primary' : 'btn-danger'}`} onClick={toggleSuspend} disabled={busy}>
          {busy ? 'Working…' : suspended ? 'Reactivate organization' : 'Suspend organization'}
        </button>
      </div>

      {error && <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,.13)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, color: '#EF4444', fontSize: 13, marginBottom: 14 }}>{error}</div>}

      {/* Details */}
      <div className="card-title" style={{ borderBottom: '1px solid #26262B', paddingBottom: 12, marginBottom: 0 }}>Details</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, background: '#26262B', border: '1px solid #26262B', borderRadius: 10, overflow: 'hidden', marginTop: 14, marginBottom: 24 }}>
        <Cell label="Plan" value={org.plan || org.plan_name} />
        <Cell label="Workspace" value={org.slug ? `nexcarat.com/${org.slug}` : '-'} />
        <Cell label="Users" value={org.user_count ?? users.length} />
        <Cell label="Branches" value={org.branch_count} />
        <Cell label="Contact email" value={org.contact_email} />
        <Cell label="Contact phone" value={org.contact_phone} />
        <Cell label="Country" value={org.country} />
        <Cell label="Joined" value={fmtDate(org.created_at)} />
      </div>

      {/* Subscription */}
      <div className="flex items-center justify-between" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #26262B', paddingBottom: 12, marginBottom: 0 }}>
        <div className="card-title">Subscription</div>
        {!editSub && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-primary btn-sm" onClick={openEdit} disabled={!!subBusy}>{sub ? 'Edit subscription' : 'Set up subscription'}</button>
            {sub && (
              <>
                <input type="number" min={1} value={extendDays} onChange={e => setExtendDays(e.target.value)}
                  style={{ width: 56, padding: '6px 8px', background: '#1E1E22', border: '1px solid #34343A', borderRadius: 7, color: '#FAFAFA', fontFamily: 'monospace', fontSize: 12, textAlign: 'center' }} />
                <button className="btn btn-secondary btn-sm" onClick={extendTrial} disabled={subBusy === 'extend'}>{subBusy === 'extend' ? '…' : 'Extend trial (days)'}</button>
                {(sub.status === 'active' || sub.status === 'trialing' || sub.status === 'past_due') && (
                  <button className="btn btn-danger btn-sm" onClick={() => setConfirmCancel(true)} disabled={subBusy === 'cancel'}>{subBusy === 'cancel' ? '…' : 'Cancel now'}</button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {editSub && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 14, padding: '18px 16px', background: '#161619', border: '1px solid #26262B', borderRadius: 10, marginTop: 14, marginBottom: 24 }}>
          <div>
            <div className="field-label" style={{ marginBottom: 6 }}>Plan</div>
            <select className="field-select" value={editSub.plan} onChange={e => setEditSub(s => ({ ...s, plan: e.target.value }))} style={{ width: 150 }}>
              <option value="starter">Starter</option>
              <option value="pro">Pro</option>
            </select>
          </div>
          <div>
            <div className="field-label" style={{ marginBottom: 6 }}>Seats</div>
            <input type="number" min={1} value={editSub.seats} onChange={e => setEditSub(s => ({ ...s, seats: e.target.value }))}
              style={{ width: 80, padding: '8px 10px', background: '#1E1E22', border: '1px solid #34343A', borderRadius: 7, color: '#FAFAFA', fontFamily: 'monospace', fontSize: 13, textAlign: 'center' }} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#D4D4D8', cursor: 'pointer', paddingBottom: 8 }}>
            <input type="checkbox" checked={editSub.comp} onChange={e => setEditSub(s => ({ ...s, comp: e.target.checked }))} style={{ width: 15, height: 15, accentColor: '#3B82F6' }} />
            Free / comp (no charge)
          </label>
          {editSub.comp && (
            <div>
              <div className="field-label" style={{ marginBottom: 6 }}>Access until *</div>
              <input type="date" value={editSub.comp_until} onChange={e => setEditSub(s => ({ ...s, comp_until: e.target.value }))}
                style={{ padding: '7px 10px', background: '#1E1E22', border: '1px solid #34343A', borderRadius: 7, color: '#FAFAFA', fontFamily: 'monospace', fontSize: 12 }} />
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', paddingBottom: 2 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setEditSub(null)} disabled={subBusy === 'save'}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={saveSub} disabled={subBusy === 'save' || !editSub.plan || !(Number(editSub.seats) >= 1) || !editSub.comp || !editSub.comp_until}>{subBusy === 'save' ? 'Saving…' : 'Save subscription'}</button>
          </div>
        </div>
      )}

      {!sub ? (
        <div style={{ padding: '18px 2px', color: '#71717A', fontSize: 13 }}>No subscription on record for this organization.</div>
      ) : !editSub ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, background: '#26262B', border: '1px solid #26262B', borderRadius: 10, overflow: 'hidden', marginTop: 14, marginBottom: 24 }}>
          <Cell label="Plan" value={(sub.plan || sub.plan_id || sub.plan_name || '-')} />
          <Cell label="Status" value={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <StatusPill status={['canceled', 'incomplete_expired', 'past_due', 'unpaid'].includes(sub.status) ? 'on_hold' : 'active'} label={(sub.status || '-').replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())} />
              {sub.comp && <span style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 100, background: 'rgba(34,197,94,.14)', color: '#22C55E' }}>Comped</span>}
            </span>
          } />
          <Cell label="Seats" value={sub.seats ?? sub.seats_included} />
          <Cell label="Period ends" value={fmtDate(sub.current_period_end)} />
          <Cell label="Trial ends" value={fmtDate(sub.trial_end)} />
          <Cell label="Cancels at period end" value={sub.cancel_at_period_end ? 'Yes' : 'No'} />
          <Cell label="Interval" value={sub.interval || '-'} />
          <Cell label="Stripe sub" value={<span style={{ fontFamily: 'monospace', fontSize: 11 }}>{sub.stripe_subscription_id || sub.subscription_id || '-'}</span>} />
        </div>
      ) : null}

      {/* Add-ons */}
      <div className="card-title" style={{ borderBottom: '1px solid #26262B', paddingBottom: 12, marginBottom: 14 }}>Add-ons</div>
      {addonHint && (
        <div style={{ padding: '10px 14px', background: 'rgba(245,158,11,.12)', border: '1px solid rgba(245,158,11,.3)', borderRadius: 8, color: '#F59E0B', fontSize: 12.5, marginBottom: 14 }}>{addonHint}</div>
      )}
      <div style={{ marginBottom: 24 }}>
        {ADDON_CATALOG.map(c => {
          const st = addons.find(a => a.addon === c.key) || {};
          const busy = addonBusy === c.key;
          return (
            <div key={c.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '14px 0', borderBottom: '1px solid #26262B' }}>
              <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: '#FAFAFA' }}>{c.name}</span>
                  {st.entitled
                    ? <span style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 100, background: 'rgba(34,197,94,.14)', color: '#22C55E' }}>Entitled</span>
                    : <span style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 100, background: '#26262B', color: '#A1A1AA' }}>Off</span>}
                  {st.comp && <span style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 100, background: 'rgba(59,130,246,.14)', color: '#60A5FA' }}>Comped{st.tier_name || st.tier ? ` · ${st.tier_name || st.tier}` : ''}</span>}
                </div>
                <div style={{ fontSize: 12, color: '#71717A', marginTop: 3 }}>{c.desc}</div>
                {st.is_active && (
                  <div style={{ fontSize: 11, color: '#71717A', marginTop: 3 }}>
                    {st.comp ? 'Comped · ' : ''}active until {st.current_period_end ? fmtDate(st.current_period_end) : 'Pro subscription ends'}{st.cancel_at_period_end ? ' · cancels at period end' : ''}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                {st.is_active ? (
                  <button className="btn btn-danger btn-sm" onClick={() => revokeAddon(c.key)} disabled={busy}>{busy ? '…' : 'Revoke now'}</button>
                ) : !onPro ? (
                  <span style={{ fontSize: 11.5, color: '#71717A', maxWidth: 220, textAlign: 'right' }}>Requires Pro first - use Edit subscription above.</span>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {c.key === 'outbound_api' && apiTiers.length > 0 && (
                        <select value={addonTier[c.key] || apiTiers[0].key} onChange={e => setAddonTier(t => ({ ...t, [c.key]: e.target.value }))}
                          style={{ padding: '7px 10px', background: '#1E1E22', border: '1px solid #34343A', borderRadius: 7, color: '#FAFAFA', fontFamily: 'monospace', fontSize: 12 }}>
                          {apiTiers.map(t => <option key={t.key} value={t.key}>{t.name}</option>)}
                        </select>
                      )}
                      <input type="date" value={addonUntil[c.key] || ''} onChange={e => setAddonUntil(u => ({ ...u, [c.key]: e.target.value }))}
                        style={{ padding: '7px 10px', background: '#1E1E22', border: '1px solid #34343A', borderRadius: 7, color: '#FAFAFA', fontFamily: 'monospace', fontSize: 12 }} />
                      <button className="btn btn-primary btn-sm" onClick={() => compAddon(c.key)} disabled={busy}>{busy ? 'Comping…' : 'Comp free'}</button>
                    </div>
                    <span style={{ fontSize: 10.5, color: '#71717A' }}>
                      {c.key === 'outbound_api' && apiTiers.length > 0 ? 'Defaults to the first tier above. ' : ''}Blank end date = active until Pro subscription ends
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Invoices */}
      <div className="card-title" style={{ borderBottom: '1px solid #26262B', paddingBottom: 12, marginBottom: 14 }}>Invoices ({invoices.length})</div>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table className="tbl" style={{ width: '100%' }}>
          <thead><tr><th>Invoice</th><th>Date</th><th style={{ textAlign: 'right' }}>Amount</th><th style={{ textAlign: 'center' }}>Status</th><th></th></tr></thead>
          <tbody>
            {invoices.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: '32px', color: '#71717A' }}>No invoices</td></tr>
            ) : invoices.map(inv => (
              <tr key={inv.id || inv.number}>
                <td className="tbl-mono">{inv.number || inv.id}</td>
                <td>{fmtDate(inv.created || inv.period_start || inv.date)}</td>
                <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{fmtMoney(inv.amount_paid ?? inv.amount, inv.currency)}</td>
                <td style={{ textAlign: 'center', textTransform: 'capitalize', color: inv.status === 'paid' ? '#22C55E' : '#F59E0B', fontSize: 12 }}>{inv.status || '-'}</td>
                <td style={{ textAlign: 'right' }}>
                  {(inv.invoice_pdf || inv.hosted_invoice_url) && (
                    <a href={inv.invoice_pdf || inv.hosted_invoice_url} target="_blank" rel="noreferrer" style={{ color: '#3B82F6', fontSize: 12 }}>View ↗</a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Users */}
      <div className="card-title" style={{ borderBottom: '1px solid #26262B', paddingBottom: 12, marginBottom: 14 }}>Users ({users.length})</div>
      <div style={{ overflowX: 'auto' }}>
        <table className="tbl" style={{ width: '100%' }}>
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Last login</th></tr></thead>
          <tbody>
            {users.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: '32px', color: '#71717A' }}>No users</td></tr>
            ) : users.map(u => (
              <tr key={u.id}>
                <td style={{ color: '#FAFAFA' }}>{[u.first_name, u.last_name].filter(Boolean).join(' ') || '-'}</td>
                <td style={{ color: '#A1A1AA', fontFamily: 'monospace', fontSize: 12 }}>{u.email}</td>
                <td style={{ color: '#D4D4D8', textTransform: 'capitalize' }}>{(u.role || '').replace('_', ' ')}</td>
                <td><StatusPill status={u.is_active === false ? 'on_hold' : 'active'} label={u.is_active === false ? 'Inactive' : 'Active'} /></td>
                <td className="tbl-mono">{u.last_login ? fmtDate(u.last_login) : 'Never'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={confirmCancel} onClose={() => setConfirmCancel(false)} title="Cancel subscription" size="sm">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p style={{ fontSize: 13, color: '#A1A1AA', margin: 0, lineHeight: 1.6 }}>Cancel this organization's subscription now? They'll be blocked on the next request.</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button className="btn btn-ghost" onClick={() => setConfirmCancel(false)}>Cancel</button>
            <button className="btn btn-danger" onClick={cancelSub}>Cancel subscription</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
